import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createAssistantSession,
  chatWithAssistant,
  getAssistantTurn,
  cancelAssistantTurn,
} from "../api/client";
import AssistantMessageContent from "./AssistantMessageContent";
import AssistantArtifactCard from "./AssistantArtifactCard";
import AssistantConfirmationCard from "./AssistantConfirmationCard";
import AssistantRecordLinksCard from "./AssistantRecordLinksCard";
import AssistantTemplateParamsCard from "./AssistantTemplateParamsCard";
import AssistantTurnTimeline from "./AssistantTurnTimeline";
import { isPartnerAssistantContext } from "../utils/partnerPortal";
import { useEffectivePermissions, toSet } from "../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { useChannels } from "../hooks/useChannels";
import {
  buildAssistantSuggestions,
  deriveAssistantChannelCapabilities,
} from "../utils/assistantSuggestionExamples";
import { navigateAssistantHref } from "../utils/assistantNavigation";
import {
  WS_KEY,
  getActiveWorkspaceSlug,
  readWorkspaceSessionJson,
  writeWorkspaceSessionJson,
  removeWorkspaceSession,
} from "../utils/workspaceStorage";

/** Intervalo do polling. Turno agêntico leva de segundos a ~1 minuto. */
const POLL_INTERVAL_MS = 1200;
/** Teto de espera: acima disso é falha, e travar o campo não ajuda ninguém. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function useLocalDateFormatter() {
  const { i18n } = useTranslation();
  return React.useCallback(
    (value) => {
      if (!value) return "";
      const s = String(value).trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const loc = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";
        return d.toLocaleDateString(loc);
      }
      return s;
    },
    [i18n.language]
  );
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

const PERIOD_PRESETS = [
  { key: "7", days: 7 },
  { key: "30", days: 30 },
  { key: "90", days: 90 },
];

/** Snapshot leve: sessionId + mensagens + período (sessionStorage por workspace). */
function assistantStorageSlug(partnerAssistant) {
  return getActiveWorkspaceSlug() || (partnerAssistant ? "partner" : "app");
}

function loadAssistantSnapshot(slug) {
  const raw = readWorkspaceSessionJson(WS_KEY.PLATFORM_AI_CHAT, null, slug);
  if (!raw || typeof raw !== "object") return null;
  const sessionId = String(raw.sessionId || "").trim();
  const chatMessages = Array.isArray(raw.chatMessages) ? raw.chatMessages : [];
  if (!sessionId && !chatMessages.length) return null;
  return {
    sessionId,
    meta: raw.meta && typeof raw.meta === "object" ? raw.meta : null,
    chatMessages,
    period:
      raw.period?.start && raw.period?.end
        ? { start: String(raw.period.start), end: String(raw.period.end) }
        : null,
  };
}

function saveAssistantSnapshot(slug, { sessionId, meta, chatMessages, period }) {
  const payload = {
    sessionId: String(sessionId || ""),
    meta: meta || null,
    chatMessages: Array.isArray(chatMessages) ? chatMessages.slice(-40) : [],
    period: period?.start && period?.end ? period : null,
    savedAt: Date.now(),
  };
  writeWorkspaceSessionJson(WS_KEY.PLATFORM_AI_CHAT, payload, slug);
}

function clearAssistantSnapshot(slug) {
  removeWorkspaceSession(WS_KEY.PLATFORM_AI_CHAT, slug);
}

const PlatformAiChatDialog = ({ open, onClose }) => {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const location = useLocation();
  const fmtDate = useLocalDateFormatter();
  const partnerAssistant = isPartnerAssistantContext();
  const { data: permsData } = useEffectivePermissions();
  const { segment } = useWorkspaceSegment();
  const { data: channelsData, isSuccess: channelsReady } = useChannels();
  const workspaceSlug = assistantStorageSlug(partnerAssistant);

  const initialSnapRef = React.useRef(null);
  if (initialSnapRef.current === null) {
    initialSnapRef.current = loadAssistantSnapshot(workspaceSlug) || { empty: true };
  }
  const boot = initialSnapRef.current.empty ? null : initialSnapRef.current;

  const [sessionId, setSessionId] = React.useState(() => boot?.sessionId || "");
  const [meta, setMeta] = React.useState(() => boot?.meta || null);
  const [chatMessages, setChatMessages] = React.useState(() => boot?.chatMessages || []);
  const [input, setInput] = React.useState("");
  const [period, setPeriod] = React.useState(() => boot?.period || null);
  const [periodAnchor, setPeriodAnchor] = React.useState(null);
  const [steps, setSteps] = React.useState([]);
  const [pending, setPending] = React.useState(false);
  const [turnId, setTurnId] = React.useState("");
  const listRef = React.useRef(null);
  const abortedRef = React.useRef(false);
  const turnIdRef = React.useRef("");
  const workspaceGenRef = React.useRef(0);
  const bootedWorkspaceRef = React.useRef(workspaceSlug);
  const skipPersistRef = React.useRef(false);

  React.useEffect(() => {
    turnIdRef.current = turnId;
  }, [turnId]);

  const channelCapabilities = React.useMemo(() => {
    if (partnerAssistant) {
      return { channelsLoaded: true, hasWhatsapp: false, hasEmail: false };
    }
    if (!channelsReady) {
      return { channelsLoaded: false, hasWhatsapp: false, hasEmail: false };
    }
    return deriveAssistantChannelCapabilities(channelsData?.channels);
  }, [partnerAssistant, channelsReady, channelsData?.channels]);

  const suggestions = React.useMemo(
    () =>
      buildAssistantSuggestions({
        scopes: toSet(permsData?.scopes),
        segment,
        partner: partnerAssistant,
        channelCapabilities,
      }),
    [permsData?.scopes, segment, partnerAssistant, channelCapabilities]
  );

  const sessionMutation = useMutation(async () => createAssistantSession({}), {
    onSuccess: (data) => {
      setSessionId(String(data?.sessionId || ""));
      setMeta(data || null);
      const sessionPeriod = data?.overview?.period || data?.period || null;
      if (sessionPeriod?.start && sessionPeriod?.end) setPeriod(sessionPeriod);
      const hello = String(data?.message || "").trim();
      setChatMessages(hello ? [{ role: "assistant", content: hello }] : []);
    },
  });

  const resetConversation = React.useCallback(() => {
    skipPersistRef.current = true;
    clearAssistantSnapshot(workspaceSlug);
    setSessionId("");
    setMeta(null);
    setChatMessages([]);
    setInput("");
    setSteps([]);
    setPending(false);
    setTurnId("");
    setPeriod(null);
    sessionMutation.mutate();
    setTimeout(() => {
      skipPersistRef.current = false;
    }, 0);
  }, [sessionMutation, workspaceSlug]);

  const showSuggestions =
    !sessionMutation.isLoading &&
    !pending &&
    Boolean(sessionId) &&
    suggestions.length > 0 &&
    !chatMessages.some((m) => m.role === "user");

  // Abre: só cria sessão se ainda não houver contexto (fecha/abre mantém).
  React.useEffect(() => {
    if (!open) return;
    if (sessionId) return;
    if (sessionMutation.isLoading) return;
    sessionMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Troca de workspace: limpa conversa (contexto de outro tenant).
  // skipPersistRef só volta a false no próximo tick — evita gravar o snapshot
  // do tenant A na chave do tenant B antes do state limpar.
  React.useEffect(() => {
    if (bootedWorkspaceRef.current === workspaceSlug) return;
    bootedWorkspaceRef.current = workspaceSlug;
    workspaceGenRef.current += 1;
    initialSnapRef.current = { empty: true };
    skipPersistRef.current = true;
    abortedRef.current = true;
    const inflightTurn = turnIdRef.current;
    if (inflightTurn) {
      cancelAssistantTurn(inflightTurn).catch(() => {});
    }
    clearAssistantSnapshot(workspaceSlug);
    setSessionId("");
    setMeta(null);
    setChatMessages([]);
    setInput("");
    setSteps([]);
    setPending(false);
    setTurnId("");
    setPeriod(null);
    if (open) sessionMutation.mutate();
    setTimeout(() => {
      skipPersistRef.current = false;
      abortedRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlug]);

  // Persiste conversa (sessionStorage por workspace — some no logout).
  React.useEffect(() => {
    if (skipPersistRef.current) return;
    if (bootedWorkspaceRef.current !== workspaceSlug) return;
    if (!sessionId && !chatMessages.length) {
      clearAssistantSnapshot(workspaceSlug);
      return;
    }
    saveAssistantSnapshot(workspaceSlug, { sessionId, meta, chatMessages, period });
  }, [sessionId, meta, chatMessages, period, workspaceSlug]);

  // keepMounted: fechar o diálogo não aborta o turno — o polling segue e a
  // resposta entra no histórico persistido. Só cancelamento explícito aborta.

  React.useEffect(() => {
    if (!listRef.current) return;
    try {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    } catch {}
  }, [chatMessages, steps, pending]);

  const goAppHref = React.useCallback(
    (href) => {
      if (!href) return;
      navigateAssistantHref(href, {
        pathname: location.pathname,
        navigate,
        onBeforeNavigate: () => onClose?.(),
      });
    },
    [location.pathname, navigate, onClose]
  );

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /** Acompanha um turno até o estado terminal, alimentando a timeline. */
  const followTurn = async (id) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (!abortedRef.current && Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      if (abortedRef.current) return null;

      let turn;
      try {
        turn = await getAssistantTurn(id);
      } catch {
        // Falha de rede isolada não encerra o turno: ele segue no servidor.
        continue;
      }

      if (Array.isArray(turn?.steps)) setSteps(turn.steps);

      if (turn?.status === "completed") return turn;
      if (turn?.status === "failed") {
        throw new Error(turn.error || t("platformAi.chatError"));
      }
      if (turn?.status === "cancelled") return { cancelled: true };
    }

    if (abortedRef.current) return null;
    throw new Error(
      t("platformAi.turnTimeout", {
        defaultValue: "A análise demorou mais que o esperado. Tente um recorte menor.",
      })
    );
  };

  const handleSend = async (rawMessage) => {
    let msg = String(typeof rawMessage === "string" ? rawMessage : input || "").trim();
    if (!msg || pending || !sessionId) return;
    abortedRef.current = false;
    const sendGen = workspaceGenRef.current;
    const stillCurrentWorkspace = () => sendGen === workspaceGenRef.current;

    // Se o usuário digitar só o número da opção, expande para o reply do card.
    const asOption = /^\d{1,2}$/.exec(msg);
    if (asOption) {
      const n = Number(asOption[0]);
      const lastAssistant = [...chatMessages].reverse().find((m) => m?.role === "assistant");
      const choiceArt = (lastAssistant?.artifacts || []).find(
        (a) =>
          a &&
          (a.kind === "choice_options" || a.selectable === true) &&
          Array.isArray(a.items) &&
          a.items.length
      );
      if (choiceArt) {
        const item =
          choiceArt.items.find((it) => Number(it.optionIndex) === n) ||
          choiceArt.items[n - 1];
        const reply = String(item?.reply || "").trim();
        if (reply) msg = reply;
      }
    }

    const next = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(next);
    setInput("");
    setSteps([]);
    setPending(true);

    try {
      const history = next.slice(-12).map((m) => ({ role: m.role, content: m.content }));
      const resp = await chatWithAssistant({
        sessionId,
        userMessage: msg,
        history,
        ...(period?.start && period?.end ? { period } : {}),
      });
      if (!stillCurrentWorkspace()) return;

      // Turno assíncrono: a resposta chega pelo polling. Sem worker (dev
      // local), o backend já devolve a resposta pronta no mesmo POST.
      let final = resp;
      if (resp?.turnId) {
        setTurnId(String(resp.turnId));
        final = await followTurn(String(resp.turnId));
        if (!stillCurrentWorkspace()) return;
        if (!final) return;
        if (final.cancelled) {
          setChatMessages((cur) => [
            ...cur,
            {
              role: "assistant",
              content: t("platformAi.turnCancelled", { defaultValue: "Análise cancelada." }),
            },
          ]);
          return;
        }
      }

      const reply =
        String(final?.reply || final?.message || "").trim() || t("platformAi.replyFallback");

      setChatMessages((cur) => [
        ...cur,
        {
          role: "assistant",
          content: reply,
          artifacts: Array.isArray(final?.artifacts) ? final.artifacts : [],
          steps: Array.isArray(final?.steps) ? final.steps : [],
        },
      ]);
    } catch (e) {
      if (!stillCurrentWorkspace()) return;
      const emsg =
        (typeof e?.message === "string" && e.message) ||
        (typeof e?.error === "string" && e.error) ||
        t("platformAi.chatError");
      setChatMessages((cur) => [...cur, { role: "assistant", content: emsg }]);
    } finally {
      if (!stillCurrentWorkspace()) return;
      setPending(false);
      setTurnId("");
      setSteps([]);
    }
  };

  const handleCancel = async () => {
    if (!turnId) return;
    abortedRef.current = true;
    try {
      await cancelAssistantTurn(turnId);
    } catch {
      // Cancelamento é cooperativo: se a chamada falhar, o turno termina só.
    }
  };

  const applyPreset = (days) => {
    setPeriod({ start: isoDaysAgo(days - 1), end: today() });
    setPeriodAnchor(null);
  };

  const projects = Array.isArray(meta?.overview?.projects) ? meta.overview.projects : [];
  const projectList =
    projects.length > 0
      ? `${projects.slice(0, 3).join(", ")}${projects.length > 3 ? "…" : ""}`
      : "";

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md" keepMounted>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <AutoAwesomeIcon fontSize="small" />
        <Box component="span" sx={{ flex: 1 }}>
          {partnerAssistant
            ? t("platformAi.titlePartner", { defaultValue: "Assistente do representante" })
            : t("platformAi.title")}
        </Box>
        {(sessionId || chatMessages.length > 0) && (
          <Tooltip
            title={t("platformAi.clearConversation", {
              defaultValue: "Limpar conversa",
            })}
          >
            <span>
              <IconButton
                size="small"
                onClick={resetConversation}
                disabled={pending || sessionMutation.isLoading}
                aria-label={t("platformAi.clearConversation", {
                  defaultValue: "Limpar conversa",
                })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: { xs: "78vh", md: "70vh" },
        }}
      >
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          {t("platformAi.disclaimer")}
        </Alert>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip
            size="small"
            label={
              partnerAssistant
                ? t("platformAi.chipPartnerPortfolio", {
                    defaultValue: "Visão consolidada dos seus workspaces",
                  })
                : t("platformAi.chipAggregated")
            }
            variant="outlined"
          />
          {period?.start && period?.end && (
            <Chip
              size="small"
              icon={partnerAssistant ? undefined : <CalendarMonthIcon />}
              label={t("platformAi.chipPeriod", {
                start: fmtDate(period.start),
                end: fmtDate(period.end),
              })}
              variant="outlined"
              // Trocar o período vale por pergunta: não recria a sessão.
              onClick={partnerAssistant ? undefined : (e) => setPeriodAnchor(e.currentTarget)}
              clickable={!partnerAssistant}
            />
          )}
          {!partnerAssistant && projects?.length > 0 && (
            <Chip
              size="small"
              label={t("platformAi.chipProjects", { list: projectList })}
              variant="outlined"
            />
          )}
          {partnerAssistant && Number(meta?.overview?.workspaceCount || 0) > 0 ? (
            <Chip
              size="small"
              label={t("platformAi.chipPartnerWorkspaces", {
                defaultValue: "{{count}} workspaces",
                count: Number(meta.overview.workspaceCount),
              })}
              variant="outlined"
            />
          ) : null}
        </Box>

        <Menu
          anchorEl={periodAnchor}
          open={!!periodAnchor}
          onClose={() => setPeriodAnchor(null)}
        >
          {PERIOD_PRESETS.map((preset) => (
            <MenuItem key={preset.key} onClick={() => applyPreset(preset.days)}>
              {t("platformAi.periodPresetDays", {
                defaultValue: "Últimos {{count}} dias",
                count: preset.days,
              })}
            </MenuItem>
          ))}
        </Menu>

        {sessionMutation.isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{t("platformAi.sessionLoading")}</Typography>
          </Box>
        )}

        {sessionMutation.isError && (
          <Alert severity="warning" variant="outlined">
            {t("platformAi.sessionError")}
          </Alert>
        )}

        <Divider sx={{ my: 0.5 }} />

        <Box
          ref={listRef}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
            flex: "1 1 auto",
            overflow: "auto",
            bgcolor: "background.paper",
          }}
        >
          {chatMessages.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {partnerAssistant
                ? t("platformAi.emptyHintPartner", {
                    defaultValue:
                      "Pergunte sobre a visão consolidada da sua carteira ou como usar o portal do representante.",
                  })
                : t("platformAi.emptyHint")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {chatMessages.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "92%",
                    width: m.artifacts?.length ? "92%" : "auto",
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: m.role === "user" ? "primary.main" : "grey.100",
                      color: m.role === "user" ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    <AssistantMessageContent
                      content={m.content}
                      onNavigate={goAppHref}
                    />
                    {(m.artifacts || []).map((artifact, aIdx) =>
                      artifact?.kind === "pending_confirmation" ? (
                        <AssistantConfirmationCard
                          key={artifact.proposalUuid || artifact.artifactUuid}
                          artifact={artifact}
                          onNavigate={() => onClose?.()}
                        />
                      ) : artifact?.kind === "record_links" ||
                        artifact?.kind === "choice_options" ? (
                        <AssistantRecordLinksCard
                          key={`links-${artifact.module || artifact.reason || "opt"}-${aIdx}`}
                          artifact={artifact}
                          onNavigate={() => onClose?.()}
                          onChoose={(text) => {
                            if (!pending) handleSend(text);
                          }}
                        />
                      ) : artifact?.kind === "template_params" ? (
                        <AssistantTemplateParamsCard
                          key={`tpl-params-${artifact.templateId || aIdx}`}
                          artifact={artifact}
                          onDraft={(text) => {
                            if (!pending) setInput(String(text || ""));
                          }}
                        />
                      ) : (
                        <AssistantArtifactCard
                          key={artifact.artifactUuid || artifact.url || `art-${aIdx}`}
                          artifact={artifact}
                        />
                      )
                    )}
                  </Box>
                </Box>
              ))}

              {pending && (
                <Box sx={{ alignSelf: "flex-start", maxWidth: "92%" }}>
                  <AssistantTurnTimeline steps={steps} running />
                  {!steps.length && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2">{t("platformAi.typing")}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {showSuggestions && (
            <Box sx={{ mt: chatMessages.length ? 1.5 : 1.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("platformAi.suggestTitle", { defaultValue: "Experimente perguntar" })}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {suggestions.map((item) => (
                  <Chip
                    key={item.id}
                    size="small"
                    clickable
                    variant="outlined"
                    color="primary"
                    label={t(item.labelKey)}
                    onClick={() => handleSend(t(item.promptKey))}
                    disabled={!sessionId || pending}
                    sx={{ maxWidth: "100%" }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            pt: 0.5,
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
          }}
        >
          <TextField
            size="small"
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("platformAi.inputPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={!sessionId || sessionMutation.isLoading}
          />
          {pending && turnId ? (
            <IconButton
              color="warning"
              onClick={handleCancel}
              title={t("platformAi.cancelTurn", { defaultValue: "Interromper análise" })}
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              onClick={() => handleSend()}
              disabled={!input.trim() || pending || !sessionId}
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", gap: 1 }}>
        <Button
          variant="text"
          color="inherit"
          startIcon={<DeleteOutlineIcon />}
          onClick={resetConversation}
          disabled={pending || sessionMutation.isLoading || (!sessionId && !chatMessages.length)}
        >
          {t("platformAi.clearConversation", { defaultValue: "Limpar conversa" })}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t("platformAi.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlatformAiChatDialog;
